import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import { Customer, InvoiceDraft } from '../../types';
import { getFirebaseFirestore } from '../firebase';
import {
  ensureFirebaseUid,
  resolveUid,
  toFirestoreData,
} from './idb';
import { sortByCreatedAsc } from '../sort';

function customersCol(uid: string) {
  const fs = getFirebaseFirestore();
  if (!fs) throw new Error('Firestore is not configured');
  return collection(fs, 'users', uid, 'customers');
}

function sortCustomers(customers: Customer[]): Customer[] {
  return sortByCreatedAsc(customers);
}

function createCustomerId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `cus-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeCustomerName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function billToFromInvoice(
  invoice: InvoiceDraft,
): { name: string; address: string } | null {
  const addressRaw = (invoice.toAddress || '').trim();
  const nameFromField = (invoice.toName || '').trim();
  const name = nameFromField || addressRaw.split('\n')[0]?.trim() || '';
  if (!name) return null;
  return {
    name,
    address: addressRaw || name,
  };
}

export function invoiceCustomerId(invoice: InvoiceDraft): string | null {
  const id = (invoice.customerId || '').trim();
  return id || null;
}

function normalizeCustomerDoc(c: Customer): Customer {
  return {
    ...c,
    email: c.email || '',
    company: c.company || '',
    phone: c.phone || '',
    address: c.address || '',
    shipTo: c.shipTo || '',
    name: c.name || '',
    createdAt: c.createdAt || c.updatedAt || '',
  };
}

export async function saveCustomer(
  customer: Customer,
  uid?: string | null,
): Promise<void> {
  const owner = resolveUid(uid);
  if (!owner) {
    throw new Error('Sign in required to save customers');
  }
  await ensureFirebaseUid(owner);
  await setDoc(doc(customersCol(owner), customer.id), toFirestoreData(customer));
}

export async function getCustomer(
  id: string,
  uid?: string | null,
): Promise<Customer | null> {
  const owner = resolveUid(uid);
  if (!owner || !id) return null;
  await ensureFirebaseUid(owner);
  const snap = await getDoc(doc(customersCol(owner), id));
  if (!snap.exists()) return null;
  return normalizeCustomerDoc(snap.data() as Customer);
}

export async function getAllCustomers(uid?: string | null): Promise<Customer[]> {
  const owner = resolveUid(uid);
  if (!owner) return [];
  await ensureFirebaseUid(owner);
  const snap = await getDocs(customersCol(owner));
  return sortCustomers(
    snap.docs.map((d) => normalizeCustomerDoc(d.data() as Customer)),
  );
}

export async function deleteCustomer(
  id: string,
  uid?: string | null,
): Promise<void> {
  const owner = resolveUid(uid);
  if (!owner) {
    throw new Error('Sign in required to delete customers');
  }
  await ensureFirebaseUid(owner);
  await deleteDoc(doc(customersCol(owner), id));
}

export async function findCustomerIdForInvoice(
  invoice: InvoiceDraft,
  uid?: string | null,
): Promise<string | null> {
  const owner = resolveUid(uid);
  if (!owner) return null;

  const linkedId = invoiceCustomerId(invoice);
  if (linkedId) {
    const byId = await getCustomer(linkedId, owner);
    if (byId) return byId.id;
  }

  const billTo = billToFromInvoice(invoice);
  if (!billTo) return null;

  const existing = await getAllCustomers(owner);
  const match = existing.find(
    (c) => normalizeCustomerName(c.name) === normalizeCustomerName(billTo.name),
  );
  return match?.id ?? null;
}

export async function upsertCustomerFromInvoice(
  invoice: InvoiceDraft,
  uid?: string | null,
): Promise<Customer | null> {
  const billTo = billToFromInvoice(invoice);
  if (!billTo) return null;

  const { name, address } = billTo;
  const company = (invoice.toCompany || '').trim();
  const phone = (invoice.toPhone || '').trim();
  const shipTo = (invoice.shipTo || '').trim();
  const now = new Date().toISOString();
  const owner = resolveUid(uid);
  if (!owner) return null;

  const linkedId = invoiceCustomerId(invoice);
  if (linkedId) {
    const byId = await getCustomer(linkedId, owner);
    if (byId) {
      const next: Customer = {
        ...byId,
        name,
        company: company || byId.company || '',
        phone: phone || byId.phone,
        address,
        shipTo: shipTo || byId.shipTo,
        updatedAt: now,
      };
      await saveCustomer(next, owner);
      return next;
    }
  }

  const existing = await getAllCustomers(owner);
  const match = existing.find(
    (c) => normalizeCustomerName(c.name) === normalizeCustomerName(name),
  );

  if (match) {
    const next: Customer = {
      ...match,
      name,
      company: company || match.company || '',
      phone: phone || match.phone,
      address,
      shipTo: shipTo || match.shipTo,
      updatedAt: now,
    };
    await saveCustomer(next, owner);
    return next;
  }

  const created: Customer = {
    id: createCustomerId(),
    name,
    email: '',
    company,
    phone,
    address,
    shipTo,
    createdAt: now,
    updatedAt: now,
  };
  await saveCustomer(created, owner);
  return created;
}
